import { useState } from 'react';
import { Lightbulb, Trash2, Users, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import type { InspirationQualityStats } from '@/utils/inspirationQuality';
import { getInspirationQuality, getInspirationInsight, getBadgeColors, getViralScoreColor } from '@/utils/inspirationQuality';

function InfoTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span
      className="relative inline-flex ml-1"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Info className="h-3 w-3 text-gray-400 cursor-help" />
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 max-w-xs w-64 bg-white shadow-md rounded-lg p-2 text-xs text-gray-600 pointer-events-none">
          {text}
        </span>
      )}
    </span>
  );
}

interface InspirationQualityCardProps {
  source: {
    id: string;
    username: string;
    total_posts_scraped: number | null;
  };
  qualityStats: InspirationQualityStats | undefined;
  onDelete: (source: { id: string; username: string }) => void;
}

export function InspirationQualityCard({ source, qualityStats, onDelete }: InspirationQualityCardProps) {
  const { t } = useLanguage();

  const quality = qualityStats
    ? getInspirationQuality(
        qualityStats.yield_rate,
        qualityStats.avg_viral_score,
        qualityStats.total_scraped,
        qualityStats.ideas_count
      )
    : null;

  const insight = qualityStats ? getInspirationInsight(qualityStats) : null;
  const badgeColors = quality ? getBadgeColors(quality.color) : null;

  const totalScraped = qualityStats?.total_scraped ?? (source.total_posts_scraped || 0);
  const totalGenerated = qualityStats?.ideas_count ?? 0;
  const avgViral = qualityStats?.avg_viral_score;

  return (
    <div className="glass-card rounded-xl md:rounded-2xl p-3 md:p-6 border border-border/50 hover:border-primary/30 hover:shadow-md transition-all">
      {/* Header row: icon + username + badge + delete */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Lightbulb className="h-4 w-4 md:h-6 md:w-6 text-primary" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm md:text-lg truncate">@{source.username}</h3>
              {quality && badgeColors && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${badgeColors.bg} ${badgeColors.text} ${badgeColors.border} flex-shrink-0`}>
                  <span>{quality.badge}</span>
                  <span className="hidden sm:inline">{quality.label}</span>
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground md:hidden">
              {source.total_posts_scraped || 0} {t('inspiration.posts')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 md:gap-6 flex-shrink-0">
          <div className="hidden md:block">
            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-1">
              <Users className="h-3 w-3" />
              <span>Postów</span>
            </div>
            <div className="font-semibold text-center text-base">{source.total_posts_scraped || 0}</div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete({ id: source.id, username: source.username })}
            className="h-8 w-8 md:h-9 md:w-9 p-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Quality stats: stats row + dual bars + insight */}
      {qualityStats && totalScraped > 0 && (
        <div className="mt-3 md:mt-4 space-y-2">
          {/* Stats row */}
          <p className="text-sm text-gray-500">
            {totalScraped} postów przejrzano → {totalGenerated} pomysłów → śr. viral {avgViral != null ? `${avgViral}%` : '\u2014'}
          </p>

          {/* Dual bars */}
          <div className="space-y-2">
            {/* Bar 1: Skuteczność */}
            <div>
              <div className="text-sm text-gray-500 mb-1 flex items-center">
                Skuteczność {qualityStats.yield_rate}%
                <InfoTooltip text="Nie każdy post nadaje się do nauki. Odfiltrowujemy reklamy, posty z muzyką bez treści i materiały zbyt krótkie lub zbyt długie. Skuteczność pokazuje ile postów z danego profilu przeszło przez nasz filtr i zamieniło się w gotowe pomysły." />
              </div>
              <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(qualityStats.yield_rate, 100)}%`,
                    backgroundColor: '#2563eb',
                  }}
                />
              </div>
            </div>

            {/* Bar 2: Viral score */}
            <div>
              <div className="text-sm text-gray-500 mb-1 flex items-center">
                Viral score {avgViral != null ? `${avgViral}%` : '\u2014'}
                <InfoTooltip text="Oceniamy jak bardzo post przebił się względem wielkości konta. Im więcej wyświetleń, komentarzy i polubień w stosunku do obserwujących, tym wyższy wynik." />
              </div>
              <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                {avgViral != null && (
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(avgViral, 100)}%`,
                      backgroundColor: getViralScoreColor(avgViral),
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Insight */}
          {insight && (
            <p className="text-xs text-muted-foreground italic">{insight}</p>
          )}
        </div>
      )}
    </div>
  );
}
